#!/usr/bin/env bun

import { Command } from "commander";
import { Database } from "bun:sqlite";
import fs from "node:fs";
import path from "node:path";

const program = new Command();
const db = new Database("checklist.sqlite", { create: true });

function ensureSchemaIsUpToDate() {
	try {
		db.exec("BEGIN IMMEDIATE TRANSACTION;");

		const checklistsTableQuery = db.query(
			"SELECT name FROM sqlite_master WHERE type='table' AND name='checklists';",
		);
		const checklistsTableExists = checklistsTableQuery.get();

		let itemsTableExistsAndNeedsMigration = false;
		const itemsTableInfo = db.query("PRAGMA table_info(items);").all() as {
			name: string;
		}[];

		if (itemsTableInfo.length > 0) {
			// items table exists
			if (!itemsTableInfo.some((col) => col.name === "checklist_id")) {
				itemsTableExistsAndNeedsMigration = true; // Exists but old schema
			}
		}

		const migrationNeeded =
			!checklistsTableExists || itemsTableExistsAndNeedsMigration;

		if (migrationNeeded) {
			console.log("Agent-checklist: Database schema is being updated...");

			// Ensure checklists table exists or is created
			db.exec(`
        CREATE TABLE IF NOT EXISTS checklists (
          id INTEGER PRIMARY KEY AUTOINCREMENT,
          name TEXT NOT NULL UNIQUE
        );
      `);

			if (itemsTableExistsAndNeedsMigration) {
				console.log(
					"Migrating existing tasks from old 'items' table schema...",
				);
				// Check if items_old_migration already exists from a failed previous attempt
				const oldMigrationTableExists = db
					.query(
						"SELECT name FROM sqlite_master WHERE type='table' AND name='items_old_migration';",
					)
					.get();
				if (oldMigrationTableExists) {
					db.exec("DROP TABLE items_old_migration;");
				}
				db.exec("ALTER TABLE items RENAME TO items_old_migration;");

				// Create new items table
				db.exec(`
          CREATE TABLE items (
            id INTEGER PRIMARY KEY AUTOINCREMENT,
            task TEXT NOT NULL,
            completed BOOLEAN DEFAULT FALSE,
            checklist_id INTEGER,
            FOREIGN KEY (checklist_id) REFERENCES checklists(id) ON DELETE CASCADE
          );
        `);

				const defaultChecklistResult = db
					.query(
						"INSERT INTO checklists (name) VALUES (?) ON CONFLICT(name) DO NOTHING RETURNING id;",
					)
					.get("Migrated Tasks") as { id: number } | null;

				let defaultChecklistId: number;

				if (defaultChecklistResult && defaultChecklistResult.id) {
					defaultChecklistId = defaultChecklistResult.id;
				} else {
					// If "Migrated Tasks" already existed, get its ID
					const existingDefaultChecklist = db
						.query("SELECT id FROM checklists WHERE name = ?")
						.get("Migrated Tasks") as { id: number } | null;
					if (!existingDefaultChecklist) {
						throw new Error(
							"Failed to create or find default checklist for migration.",
						);
					}
					defaultChecklistId = existingDefaultChecklist.id;
				}

				db.prepare(
					"INSERT INTO items (task, completed, checklist_id) SELECT task, completed, ? FROM items_old_migration;",
				).run(defaultChecklistId);
				db.exec("DROP TABLE items_old_migration;");
				console.log(
					"Tasks migrated successfully to the 'Migrated Tasks' checklist.",
				);
			} else {
				// Items table was missing or already fine (but checklists table might have been missing)
				// Ensure new items table is created if it wasn't already
				db.exec(`
          CREATE TABLE IF NOT EXISTS items (
            id INTEGER PRIMARY KEY AUTOINCREMENT,
            task TEXT NOT NULL,
            completed BOOLEAN DEFAULT FALSE,
            checklist_id INTEGER,
            FOREIGN KEY (checklist_id) REFERENCES checklists(id) ON DELETE CASCADE
          );
        `);
			}
			console.log("Database schema update process complete.");
		} else {
			// Schema is up-to-date, or it's a brand new DB.
			// Run CREATE IF NOT EXISTS for robustness, ensuring tables are there.
			db.exec(`
        CREATE TABLE IF NOT EXISTS checklists (
          id INTEGER PRIMARY KEY AUTOINCREMENT,
          name TEXT NOT NULL UNIQUE
        );
      `);
			db.exec(`
        CREATE TABLE IF NOT EXISTS items (
          id INTEGER PRIMARY KEY AUTOINCREMENT,
          task TEXT NOT NULL,
          completed BOOLEAN DEFAULT FALSE,
          checklist_id INTEGER,
          FOREIGN KEY (checklist_id) REFERENCES checklists(id) ON DELETE CASCADE
        );
      `);
		}

		db.exec("COMMIT;");
	} catch (error: unknown) {
		try {
			db.exec("ROLLBACK;");
		} catch (rollbackError) {
			console.error("Failed to rollback database transaction:", rollbackError);
		}
		console.error(
			"A critical error occurred during database schema setup/migration.",
		);
		if (error instanceof Error) {
			console.error("Error details:", error.message);
		} else {
			console.error("Unknown error details:", error);
		}
		console.error(
			"Please try backing up and then deleting your 'checklist.sqlite' file. This file is usually located where you run the agent-checklist command or in its global installation directory. Then, run the command again.",
		);
		process.exit(1);
	}
}

// Call the schema update/check function immediately after db initialization
ensureSchemaIsUpToDate();

// Define an interface for our item
interface Item {
	id: number;
	task: string;
	completed: boolean;
	checklist_id: number;
}

// Define an interface for our checklist
interface Checklist {
	id: number;
	name: string;
}

program
	.name("agent-checklist")
	.description("A CLI tool for managing a checklist for code agents.")
	.version("0.0.1");

program
	.command("create <name>")
	.description("Create a new checklist")
	.option("-a, --add <tasks...>", "Tasks to add to the new checklist")
	.action((name: string, options: { add?: string[] }) => {
		try {
			const stmt = db.prepare(
				"INSERT INTO checklists (name) VALUES (?) RETURNING id",
			);
			const result = stmt.get(name) as { id: number } | null;

			if (!result || typeof result.id !== "number") {
				console.error("Failed to create checklist: could not retrieve ID.");
				// Check if it's a unique constraint violation
				const existing = db
					.query("SELECT id FROM checklists WHERE name = ?")
					.get(name) as { id: number } | null;
				if (existing) {
					console.error(
						`A checklist with the name "${name}" already exists with ID ${existing.id}.`,
					);
				}
				return;
			}
			const checklistId = result.id;
			console.log(`Created checklist "${name}" with ID: ${checklistId}`);

			if (options.add && options.add.length > 0) {
				const insertItemStmt = db.prepare(
					"INSERT INTO items (task, checklist_id) VALUES (?, ?)",
				);
				for (const task of options.add) {
					insertItemStmt.run(task, checklistId);
					console.log(`Added task: "${task}" to checklist ${checklistId}`);
				}
			}
		} catch (error: unknown) {
			if (
				error instanceof Error &&
				error.message &&
				error.message.includes("UNIQUE constraint failed: checklists.name")
			) {
				const existing = db
					.query("SELECT id FROM checklists WHERE name = ?")
					.get(name) as { id: number } | null;
				console.error(
					`Error: A checklist with the name "${name}" already exists${
						existing ? ` with ID ${existing.id}` : ""
					}.`,
				);
			} else {
				console.error("Failed to create checklist:", error);
			}
		}
	});

program
	.command("add <checklistId> <task>")
	.description("Add a new task to a specific checklist")
	.action((checklistIdStr: string, task: string) => {
		try {
			const checklistId = Number.parseInt(checklistIdStr, 10);
			if (Number.isNaN(checklistId)) {
				console.error("Invalid checklist ID.");
				return;
			}

			// Check if checklist exists
			const checklist = db
				.query("SELECT id FROM checklists WHERE id = ?")
				.get(checklistId) as Checklist | null;
			if (!checklist) {
				console.error(`Checklist with ID ${checklistId} not found.`);
				return;
			}

			db.run("INSERT INTO items (task, checklist_id) VALUES (?, ?)", [
				task,
				checklistId,
			]);
			console.log(`Added task: "${task}" to checklist ${checklistId}`);
		} catch (error) {
			console.error("Failed to add task:", error);
		}
	});

program
	.command("list [checklistId]")
	.description("List all checklists or tasks for a specific checklist")
	.action((checklistIdStr?: string) => {
		try {
			if (checklistIdStr) {
				const checklistId = Number.parseInt(checklistIdStr, 10);
				if (Number.isNaN(checklistId)) {
					console.error("Invalid checklist ID.");
					return;
				}
				const checklist = db
					.query("SELECT * FROM checklists WHERE id = ?")
					.get(checklistId) as Checklist | null;
				if (!checklist) {
					console.error(`Checklist with ID ${checklistId} not found.`);
					return;
				}
				console.log(
					`Tasks for checklist "${checklist.name}" (ID: ${checklist.id}):`,
				);
				const items = db
					.query("SELECT * FROM items WHERE checklist_id = ?")
					.all(checklistId) as Item[];
				if (items.length === 0) {
					console.log("No tasks in this checklist.");
					return;
				}
				for (const item of items) {
					console.log(
						`[${item.completed ? "x" : " "}] ${item.id}: ${item.task}`,
					);
				}
			} else {
				const checklists = db
					.query("SELECT * FROM checklists")
					.all() as Checklist[];
				if (checklists.length === 0) {
					console.log("No checklists found. Use 'create <name>' to add one.");
					return;
				}
				console.log("Available checklists:");
				for (const list of checklists) {
					console.log(`${list.id}: ${list.name}`);
				}
			}
		} catch (error) {
			console.error("Failed to list:", error);
		}
	});

program
	.command("complete <itemId>")
	.description("Mark a task as completed")
	.action((itemIdStr: string) => {
		try {
			const itemId = Number.parseInt(itemIdStr, 10);
			if (Number.isNaN(itemId)) {
				console.error("Invalid item ID.");
				return;
			}
			// Check if item exists before updating
			const item = db
				.query("SELECT id FROM items WHERE id = ?")
				.get(itemId) as { id: number } | null;
			if (!item) {
				console.error(`Item with ID ${itemId} not found.`);
				return;
			}
			db.run("UPDATE items SET completed = TRUE WHERE id = ?", [itemId]);
			console.log(`Marked item ${itemId} as completed.`);
		} catch (error) {
			console.error("Failed to complete item:", error);
		}
	});

program
	.command("remove <itemId>")
	.description("Remove a task from a checklist")
	.action((itemIdStr: string) => {
		try {
			const itemId = Number.parseInt(itemIdStr, 10);
			if (Number.isNaN(itemId)) {
				console.error("Invalid item ID.");
				return;
			}
			// Check if item exists before deleting
			const item = db
				.query("SELECT id FROM items WHERE id = ?")
				.get(itemId) as { id: number } | null;
			if (!item) {
				console.error(`Item with ID ${itemId} not found.`);
				return;
			}
			db.run("DELETE FROM items WHERE id = ?", [itemId]);
			console.log(`Removed item ${itemId}.`);
		} catch (error) {
			console.error("Failed to remove item:", error);
		}
	});

program
	.command("remove-checklist <checklistId>")
	.alias("rmc")
	.description("Remove a checklist and all its tasks")
	.action((checklistIdStr: string) => {
		try {
			const checklistId = Number.parseInt(checklistIdStr, 10);
			if (Number.isNaN(checklistId)) {
				console.error("Invalid checklist ID.");
				return;
			}
			// Check if checklist exists before deleting
			const checklist = db
				.query("SELECT name FROM checklists WHERE id = ?")
				.get(checklistId) as { name: string } | null;
			if (!checklist) {
				console.error(`Checklist with ID ${checklistId} not found.`);
				return;
			}
			db.run("DELETE FROM checklists WHERE id = ?", [checklistId]); // Associated items will be deleted by ON DELETE CASCADE
			console.log(
				`Removed checklist "${checklist.name}" (ID: ${checklistId}) and all its tasks.`,
			);
		} catch (error) {
			console.error("Failed to remove checklist:", error);
		}
	});

program
	.command("init")
	.option(
		"-p, --platform <platform>",
		"The platform to install the rule for",
		"cursor",
	)
	.description(
		"Install the Cursor rule for this CLI to .cursor/rules/ in the current project",
	)
	.action((options) => {
		const ruleFileName = "agent_checklist_cli_usage.mdc";
		const sourcePath = path.join(import.meta.dir, "..", ruleFileName);

		// Use process.cwd() for the current project directory
		const targetDir = path.join(process.cwd(), ".cursor", "rules");
		const targetPath = path.join(targetDir, ruleFileName);

		try {
			if (!fs.existsSync(sourcePath)) {
				console.error(
					`Error: Rule file not found at ${sourcePath}. This might be an issue with the package installation.`,
				);
				return;
			}

			fs.mkdirSync(targetDir, { recursive: true });
			fs.copyFileSync(sourcePath, targetPath);
			console.log(`Successfully copied rule to ${targetPath}`);
			console.log("Please restart Cursor to see the new rule.");
		} catch (error: unknown) {
			if (error instanceof Error) {
				console.error(`Failed to install Cursor rule: ${error.message}`);
			} else {
				console.error(
					"Failed to install Cursor rule: An unknown error occurred",
				);
			}
		}
	});

program.parse(process.argv);

// Close the database connection when the program exits (optional, Bun might handle this)
// process.on('exit', () => {
//   db.close();
// });
